/** Where Appearance lives in Settings, and the widget library on its Widgets section. */
import { Navigate, useLocation, useSearchParams } from "react-router-dom";

/**
 * Settings → Appearance (#13370; Gerry, 2026-10-19: "also the endpoint for Appearance is called
 * components?"). Its old address, `/settings/components`, redirects here.
 */
export const APPEARANCE_SETTINGS_PATH = "/settings/appearance";

/** Appearance on its record pages, with the rest of the query (`&page=…&mode=edit`) after. */
export function appearanceLayoutsPath(rest = ""): string {
  return `${APPEARANCE_SETTINGS_PATH}?section=layouts${rest}`;
}

/** The library in Settings, optionally with one component picked out. */
export function componentLibraryPath(componentId?: string): string {
  const search = new URLSearchParams({ section: "library" });
  if (componentId) search.set("component", componentId);
  return `${APPEARANCE_SETTINGS_PATH}?${search.toString()}`;
}

/** The search parameter naming the dashboard a builder was opened from (#12963). */
export const BUILDER_DASHBOARD_PARAM = "dashboard";

/**
 * A private builder's address; with the dashboard it was opened from, so a reload or a shared
 * link keeps where it came from (#12963).
 */
export function componentBuilderPath(
  builderId: string,
  dashboardId?: string | null
): string {
  const path = `/rms/components/builders/${encodeURIComponent(builderId)}`;
  if (!dashboardId) return path;
  const search = new URLSearchParams({
    [BUILDER_DASHBOARD_PARAM]: dashboardId,
  });
  return `${path}?${search.toString()}`;
}

/** A dashboard's address in edit mode, where a builder sends its finished cards. */
export function dashboardEditPath(dashboardId: string): string {
  return `/rms/dashboards/${encodeURIComponent(dashboardId)}?edit=1`;
}

/** Appearance's old address, `/settings/components`, with whatever it asked for. */
export function AppearanceSettingsRedirect() {
  const { search, hash } = useLocation();
  return (
    <Navigate to={`${APPEARANCE_SETTINGS_PATH}${search}${hash}`} replace />
  );
}

/** The library's old address, `/rms/components`, kept working for links already shared. */
export function ComponentLibraryRedirect() {
  const [params] = useSearchParams();
  return (
    <Navigate
      to={componentLibraryPath(params.get("component") ?? undefined)}
      replace
    />
  );
}
